<template>
  <CheckboxInput
    v-model="leaderboardStore.onlyKnownUsers"
    class="my-4 font-thin"
    :label="t('pages.wordle.only_known_users_checkbox')"
  ></CheckboxInput>
  <h1 class="text-center text-xl font-bold text-kekkle-dark dark:text-white">
    🏆 {{ t("pages.wordle.leaderboard") }}:
  </h1>

  <spinner
    v-if="
      leaderboardStore.isFetchingLeaderboard &&
      !leaderboardStore.currentLeaderboard.length
    "
    class="mx-auto mt-16 w-8 animate-spin"
    style="color: black"
  ></spinner>

  <div
    v-else
    class="w-full rounded-sm border-2 border-black-dark bg-white p-4 text-kekkle-dark shadow-yellow-hard dark:bg-kekkle-dark-light dark:text-white"
  >
    <div
      v-for="score in scores"
      :key="score.user_id"
      class="relative mb-2 items-center gap-2 border-b border-black-dark p-2"
    >
      <div class="mb-2 flex items-center gap-3">
        <p class="text-3xl font-bold">
          <span
            v-if="authStore.authUuid === score.user_id"
            class="pointer text-xl"
            >👉</span
          >

          <span v-if="score.position === 1"> 🥇 </span>
          <span v-else-if="score.position === 2"> 🥈 </span>
          <span v-else-if="score.position === 3"> 🥉 </span>
          <span v-else> {{ score.position }}</span>
        </p>
        <div>
          <p class="">
            {{ t("pages.wordle.name") }}
            <span class="font-bold">{{ score.username }}</span>
          </p>
          <p v-if="groupMembers[score.user_id]" class="">
            {{ t("pages.wordle.nick_name") }}
            <span class="font-bold">{{
              groupMembers[score.user_id].name
            }}</span>
          </p>
          <p class="">
            {{ t("pages.wordle.score") }}
            <span class="font-bold">{{ score.new_score }}</span>
          </p>

          <div class="flex gap-4">
            <p class="">
              {{ t("pages.wordle.played") }}
              <span class="font-bold">{{
                score.matches_played?.length || 0
              }}</span>
            </p>
            <p class="">
              {{ t("pages.wordle.wins") }}
              <span class="font-bold">{{ score.has_won }}</span>
            </p>
            <p class="">
              {{ t("pages.wordle.loses") }}
              <span class="font-bold">{{ score.has_lost }}</span>
            </p>
          </div>
        </div>
        <div></div>
      </div>
      <matches-graph :matches="score.match_tries"></matches-graph>
    </div>
  </div>

  <DefaultButton
    v-if="leaderboardStore.hasMore && scores.length"
    class="mt-4"
    :loading="leaderboardStore.isFetchingLeaderboard"
    @click="loadMore"
  >
    {{ t("pages.wordle.load_more") }}
  </DefaultButton>
</template>
<script setup>
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useWordleLeaderboardStore } from "@/stores/modules/wordle/wordle-leaderboard";
import { useWordleScoresStore } from "@/stores/modules/wordle/wordle-scores";
import { useWordleMonthlyScoresStore } from "@/stores/modules/wordle/wordle-monthly-scores";
import Spinner from "@/components/Spinner.vue";
import { computed, watch } from "vue";
import MatchesGraph from "@/components/wordle/WordleMatchesGraph.vue";
import { useI18n } from "vue-i18n";
import CheckboxInput from "@/components/Inputs/CheckboxInput.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";

const { t } = useI18n({ useScope: "global" });

const authStore = useAuthStore();
const groupsStore = useGroupsStore();
const leaderboardStore = useWordleLeaderboardStore();
const scoresStore = useWordleScoresStore();
const monthlyScoresStore = useWordleMonthlyScoresStore();

leaderboardStore.fetchLeaderboard();
const groupMembers = computed(() => groupsStore.activeGroupMembers);

watch(
  () => leaderboardStore.onlyKnownUsers,
  (newValue) => {
    leaderboardStore.fetchLeaderboard();
    leaderboardStore.setOnlyKnownUsersWorldeLeaderboard(newValue);
    scoresStore.setOnlyKnownUsersWordleScores(newValue);
    monthlyScoresStore.setOnlyKnownUsersWordleMonthlyScores(newValue);
  },
);

const scores = computed(() => {
  let scores = leaderboardStore.currentLeaderboard;
  let position = 0;
  let lastScore = null;

  if (!scores || !scores.length) {
    return [];
  }

  // Create a copy to avoid mutating the store data
  const sortedScores = [...scores].sort((a, b) => b.new_score - a.new_score);

  return sortedScores.map((score) => {
    if (score.new_score !== lastScore) {
      position = position + 1;
      lastScore = score.new_score;
    }
    return {
      ...score,
      position: position,
    };
  });
});

function loadMore() {
  leaderboardStore.fetchLeaderboard(true);
}
</script>
